/**
 * @module app.cd
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { IProviderSearchFilters, countProviderFilters, ProviderService } from 'app/cd/services/provider-service';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/api/api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { fireEvents } from 'app/shared/lib/fire-events';
import { EssAccordionCustomAttribute } from 'app/shared/attributes/ess-collapse-attributes';
import { I18N } from 'aurelia-i18n';
import { IProviderSearchConfig } from 'app/cd/models/essette-interfaces';
import { IProviderSearchOptions } from 'app/cd/elements/providers/provider-search-filters';
import { determineDefaultIsContractedValue } from 'app/cd/elements/providers/provider-location-select';
import { guid } from 'app/shared/lib/guid';
import { EventAggregator } from 'aurelia-event-aggregator';
import { SetFocusEvent } from 'app/shared/lib/events';
import { OpenFlags } from 'app/shared/flags/open-flags';
import { IPopup } from 'app/shared/elements/popup-base';
import * as CD from 'app/cd/models/essette-api';
import * as Core from 'app/core/models/essette-api';

interface IProviderLocationSelectCustomEvent { providerLocation: CD.ProviderLocation }
export type ProviderLocationSelectCustomEvent = CustomEvent<IProviderLocationSelectCustomEvent>;

@autoinject()
export class ProviderLocationSearch extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object
    private searchFilters: IProviderSearchFilters = {};
    private tempSearchFilters: IProviderSearchFilters = {};
    @bindable.objectNull({ defaultBindingMode: bindingMode.toView })
    public config: IProviderSearchConfig | null = null;
    @bindable.objectNull({ defaultBindingMode: bindingMode.toView })
    public options: IProviderSearchOptions | null = null;

    @bindable.boolean
    public showActions: boolean = true;

    @bindable.array()
    protected regions: Core.AddressRegion[] = [];
    @bindable.array()
    protected genders: Core.Gender[] = [];

    public providerLocations: ReadonlyArray<CD.ProviderLocation> | null = null;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 10;

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $collapsible!: EssAccordionCustomAttribute
    protected $searchCriteriaHeader!: HTMLElement;
    protected $currentCriteriaHeader!: HTMLElement;
    @bindable
    protected $modal!: IPopup;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly $element: Element,
        private readonly service: ProviderService,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {

        this.searchFilters.isContracted = determineDefaultIsContractedValue(this.options, this.config);
        if(!this.config?.contractedSearchUrl) {
            this.options = null;
        }

        this.$collapsible.defaultIndex = 0; //and then, the collapsible's attached() shall run and open this
    }

    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////
    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        // only trigger search if there is data to sort
        if(this.providerLocations?.length) this.searchProviders();
    }

    /** Captures the selected row object (CD.ProviderLocation) and fires the item-edit and item-select events */
    protected selectProvider(providerLocation: CD.ProviderLocation): void {
        if(providerLocation && this.$element) {
            const detail: IProviderLocationSelectCustomEvent = { providerLocation: providerLocation };
            fireEvents(this.$element, ['item-edit', 'item-select'], detail);
        }
    }

    protected searchCriteriaOpened(): void {
        this.searchFilters.generalSearchTerm = null;
    }

    public async search(): Promise<void> {
        this.searchFilters = { ...this.tempSearchFilters }
        this.searchProviders();
    }

    public clear(returnFocus?: boolean): void {
        this.providerLocations = [];
        this.resultCount = 0;
        if(this.$pager) this.$pager.updatePage(0);
        this.$collapsible.open(0);
        this.searchFilters = {};
        this.tempSearchFilters = {};
        //reset isContracted to default logic, provided it was configured
        this.searchFilters.isContracted = determineDefaultIsContractedValue(this.options, this.config);
        if(returnFocus) this.events.publish(new SetFocusEvent(this.$searchCriteriaHeader));
    }


    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public submittedChanges?: any;
    public async submit(): Promise<boolean> {
        if(this.searchFilters) {
            if(this.$pager) this.$pager.updatePage(0);
            this.search();
            this.events.publish(new SetFocusEvent(this.$currentCriteriaHeader));
        }
        return true;
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    public async searchProviders(searchFilters?: IProviderSearchFilters): Promise<void> {
        if(searchFilters) { this.searchFilters = searchFilters; }
        if(countProviderFilters(this.searchFilters) === 0) {
            this.$collapsible.open(0);

            if(this.$modal.state == OpenFlags.Open) {
                this.toaster.warn().message(this.i18n.tr('ui:messages.warnings.minimumFilterRequirements')).show();
            }
            this.providerLocations = [];
            return;
        } else {
            this.$collapsible.open(1);
        }
        if(searchFilters && this.searchFilters !== searchFilters) {
            this.searchFilters = searchFilters;
        }

        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            sorting: this.sorting,
            top: this.pageSize
        };

        const includes: string[] = [
            'provider',
            'provider.primaryUser',
            'facility',
            'specialties',
            'provider.gender',
            'provider.type',
            'facility.address.physical'
        ];

        if(this.config) { this.service.configure(this.config); }
        try {
            this.state.beginLoad();
            const response = await this.service.searchProviderLocations(this.searchFilters, options, includes);
            this.providerLocations = response.results;
            this.resultCount = response.count || 0;
            if(this.resultCount === 1) {
                this.selectProvider(this.providerLocations[0]);
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
